import * as Auth from "aws-amplify/auth";
import * as H from "history";
import {
  useCallback,
  useEffect,
  useState
} from "react";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import * as moment from "moment-timezone";
import LogRocket from "logrocket";
import localforage from "localforage";
// @ts-ignore
import memoryDriver from "localforage-memoryStorageDriver";

import createDebug from "src/library/utils/debug";
import { useLocalStorage } from "src/library/hooks";
import {
  AmplifyError,
  parseAmplifyError
} from "src/utils/aws-amplify/errors";
import {
  purgeCustomers,
  setCustomer
} from "src/core/customers/store/customers.actions";
import {
  purgeDrops,
  setDrop
} from "src/core/drops/store/drops.actions";
import { purgeLists } from "src/core/lists/store/lists.actions";
import { purgeNotifications } from "src/core/notifications/store/notifications.actions";
import { purgeOrders } from "src/core/orders/store/orders.actions";
import { purgeProducts } from "src/core/products/store/products.actions";
import { purgeSchedules } from "src/core/schedules/store/schedules.actions";
import type { IStore } from "src/core/shared/store";
import {
  purgeUserRole,
  setGlobalUser,
  setGlobalUserStatus,
  setMasqueradeMode
} from "src/core/shared/store/global/global.actions";
import { purgeUsers } from "src/core/users/store/users.actions";
import useAxios from "src/core/shared/hooks/useAxios";
import { purgeAnalytics } from "src/core/analytics/store/analytics.actions";
import { ILoginEntities } from "src/core/users/api/users.api";
import { gtmEvent } from "src/library/utils/tagmanager-events";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

const debug = createDebug("AUTH");

interface UseAuthActions {
  login: (username: string, password: string) => void | any;
  loginWithMicrosoft: (idpName?: string) => void;
  loginWithGoogle: () => void;
  logout: () => void;
  checkLoggedIn: () => Promise<boolean>;
  confirmNewPassword: (user: any, password: string) => void;
  confirmMFACode: (mfaCode: string) => void;
  forgotPassword: (username: any) => void;
  confirmResetForgottenPassword: (username: string, code: string, newPassword: string) => void;
}

type ChallengName =
"CONTINUE_SIGN_IN_WITH_TOTP_SETUP" |
"CONTINUE_SIGN_IN_WITH_EMAIL_SETUP" |
"CONFIRM_SIGN_IN_WITH_TOTP_CODE" |
"CONTINUE_SIGN_IN_WITH_MFA_SELECTION" |
"CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION" |
"CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE" |
"CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED" |
"CONFIRM_SIGN_IN_WITH_SMS_CODE" |
"CONFIRM_SIGN_IN_WITH_EMAIL_CODE" |
"CONFIRM_SIGN_UP" |
"RESET_PASSWORD" |
"DONE" |
"CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION" |
"CONFIRM_SIGN_IN_WITH_PASSWORD" |
"NEW_PASSWORD_REQUIRED";

interface UseAuthState {
  user: Auth.AuthUser | null; // no typings in cognito
  challengeName?: ChallengName;
  loggedIn: boolean;
  loggingIn: boolean;
  loggedOut: boolean;
  socialLoggingIn: boolean;
  loggingOut: boolean;
  error: string | null;
  resettingPassword: boolean;
  userSuspended: boolean;
}

export const useAuth = (check = false, history: H.History<{from?: string}>, redirectOnError = false): [UseAuthActions, UseAuthState] => {
  const [ , saveTableSettings ] = useLocalStorage("table-settings");
  const [ , saveNewTableSettings ] = useLocalStorage("table-options");
  const [ , setTags ] = useLocalStorage("product-tags", "[]");

  const [ userAuthState, setUserAuthState ] = useState<UseAuthState>({
    loggedIn: false,
    user: null,
    loggingIn: true,
    loggingOut: true,
    loggedOut: false,
    socialLoggingIn: false,
    error: null,
    resettingPassword: false,
    userSuspended: false
  });

  const dispatch = useDispatch();

  const {
    currentUser, currentRole, customer, settings, masqueradeMode
  } = useMappedState(({ global, customers }: IStore) => ({
    customer: customers.customer,
    currentUser: global.user,
    currentRole: global.role,
    settings: global.settings,
    masqueradeMode: global.masqueradeMode
  }));

  const defaultError = "Could not login at this time, please try again later";

  // check if we are logged in
  useEffect(() => {
    if (check) {
      checkLoggedIn(true);
    }

    setUserAuthState({
      ...userAuthState,
      socialLoggingIn: false,
      loggingIn: false
    });
  }, []);

  useEffect(() => {
    if (currentUser && currentRole) {
      if (!currentUser.customerId || (currentUser.customerId && customer)) {
        if (process.env.REACT_APP_ENVIRONMENT !== "dev") {
          LogRocket.identify(`${currentUser.id}`, { role: currentRole.code });
        }

        setUserAuthState({
          ...userAuthState,
          loggedIn: true,
          loggingIn: false
        });

        if (check) {
          // if previous page exists, return to it
          if (history.location.state?.from && history.location.state?.from != "/logout") {
            history.push(history.location.state.from);
          } else {
            if (currentRole.type === "admin") {
              if (currentRole.code === "van-sales-admin") {
                history.push("/van-sales");
              } else {
                history.push("/admin/dashboard");
              }
            } else if (currentRole.type === "customer") {
              history.push("/dashboard");
            }
          }
        }
      }
    }
  }, [
    currentUser,
    currentRole,
    customer
  ]);

  const [ loginEntitiesResponse, getLoginEntities ] = useAxios<ILoginEntities, null>(() => {
    return {
      url: `${process.env.REACT_APP_API_URL}/core/users/me/info`,
      method: "GET",
      cache: {
        maxAge: cacheMinutesToMilliseconds(15),
        clearOnError: true
      }
    };
  }, "GET ME");

  const [ resendInviteResponse, resendInvite ] = useAxios((params: { email: string }) => {
    return {
      url: `${process.env.REACT_APP_API_URL}/core/users/resend-invite/public`,
      method: "POST",
      headers: false,
      params
    };
  }, "PUBLIC RESEND INVITE");

  useEffect(() => {
    if (loginEntitiesResponse.data) {
      // When in masquerade mode we have already authorised, set our global user and the correct role mocked
      // skip the rest of the function to avoid overwriting any of these values
      if (masqueradeMode?.active) {
        return;
      }

      if (loginEntitiesResponse.data.user.useSubdomain) {
        if (location.hostname.split(".")[ 0 ] !== loginEntitiesResponse.data.user.useSubdomain) {
          setUserAuthState({
            ...userAuthState,
            error: `You are not authorised to login here. Please login in at https://${loginEntitiesResponse.data.user.useSubdomain}.mezze.io`,
            loggingIn: false
          });
          dispatch(setGlobalUser(null));

          return;
        }
      }

      if (loginEntitiesResponse.data.user.status === "suspended") {
        dispatch(setGlobalUserStatus(true));

        setUserAuthState({
          ...userAuthState,
          error: null,
          loggingIn: false,
          userSuspended: true
        });
        Auth.signOut();
        dispatch(setGlobalUser(null));

        if (location.pathname !== "/") {
          history.push("/");
        }
      } else {
        dispatch(setGlobalUserStatus(false));
        dispatch(setGlobalUser(loginEntitiesResponse.data.user));

        if (loginEntitiesResponse.data.customer) {
          dispatch(setCustomer(loginEntitiesResponse.data.customer));
        }

        if (loginEntitiesResponse.data.branch) {
          dispatch(setDrop(loginEntitiesResponse.data.branch));
        }

        setUserAuthState({
          ...userAuthState,
          loggedIn: true
        });
      }
    }

    if (loginEntitiesResponse.error) {
      setUserAuthState({
        ...userAuthState,
        error: loginEntitiesResponse.error.message,
        loggingIn: false
      });
      Auth.signOut();
      dispatch(setGlobalUser(null));

      if (location.pathname !== "/") {
        history.push("/");
      }
    }
  }, [ loginEntitiesResponse ]);

  useEffect(() => {
    if (resendInviteResponse.data) {
      setUserAuthState({
        ...userAuthState,
        resettingPassword: false,
        error:
          "You cannot reset your password until you have signed up. We have resent you an invite email, to login please follow the instructions on the email."
      });
    } else if (resendInviteResponse.error) {
      setUserAuthState({
        ...userAuthState,
        resettingPassword: false
      });
    }
  }, [ resendInviteResponse ]);

  // Funtion to run to make sure everything is initialised
  // before we login
  const loadLoginEntities = useCallback(
    async (redirect = false) => {
      if (!currentUser || !currentRole || !currentUser.lastLoggedIn || moment.utc().isAfter(moment.utc(currentUser.lastLoggedIn).add(1, "minute"))) {
        await getLoginEntities();
      } else {
        setUserAuthState({
          ...userAuthState,
          user: await Auth.getCurrentUser(),
          loggedIn: true,
          loggingIn: false
        });

        if (redirect) {
          if (currentRole.type === "admin") {
            if (currentRole.code === "van-sales-admin") {
              history.push("/van-sales");
            } else {
              history.push("/admin/dashboard");
            }
          } else if (currentRole.type === "customer") {
            history.push("/dashboard");
          }
        }
      }
    },
    [
      currentUser,
      currentRole,
      getLoginEntities,
      userAuthState,
      history
    ]
  );

  const checkCognitoGroup = useCallback((userSession: Auth.AuthSession): boolean => {
    if (settings && settings.aws.cognito.group) {
      if (
        userSession.tokens?.accessToken.payload &&
        userSession.tokens?.accessToken.payload[ "cognito:groups" ],
        Array.isArray(userSession.tokens?.accessToken.payload[ "cognito:groups" ])
      ) {
        if (userSession.tokens?.accessToken.payload[ "cognito:groups" ].includes(settings.aws.cognito.group)) {
          return true;
        }
      }
    }

    return false;
  }, [ settings ]);

  // Handles successful sign-in after password confirmation
  const handleSuccessfulSignIn = useCallback(async () => {
    const session = await Auth.fetchAuthSession();

    if (checkCognitoGroup(session)) {
      setTimeout(() => {
        loadLoginEntities();
      }, 300);

      return;
    }

    setUserAuthState({
      ...userAuthState,
      error: "Not authorized to access this portal",
      loggingIn: false
    });
    dispatch(setGlobalUser(null));
    Auth.signOut();
  }, [
    userAuthState,
    loadLoginEntities,
    dispatch,
    checkCognitoGroup
  ]);

  // Resets a users password
  const confirmNewPassword = useCallback(
    async (user: Auth.AuthUser, password: string) => {
      setUserAuthState({
        ...userAuthState,
        error: null
      });

      Auth.confirmSignIn({ challengeResponse: password })
        .then(async auth => {
          if (auth.isSignedIn) {
            await handleSuccessfulSignIn();
          } else {
            if (auth.nextStep.signInStep) {
              setUserAuthState({
                ...userAuthState,
                challengeName: auth.nextStep.signInStep,
                user
              });
            }
          }
        })
        .catch((err: AmplifyError) => {
          const parsedErr = parseAmplifyError(err);

          setUserAuthState({
            ...userAuthState,
            error: parsedErr.details,
            loggingIn: false
          });
        });
    },
    [ userAuthState, handleSuccessfulSignIn ]
  );

  // Checks the user is logged in
  const checkLoggedIn = useCallback(
    async (redirect = false) => {
      const publicPageIsAccessible = [
        "/forgot-password",
        "/register",
        "/oauth"
      ].includes(location.pathname);

      try {
        const userSession = await Auth.fetchAuthSession();

        if (checkCognitoGroup(userSession)) {
          await loadLoginEntities(redirect);

          return true;
        } else {
          await Auth.signOut();
          debug.error("SIGNED OUT:", "Incorrect portal to group mapping");
          dispatch(purgeUsers());

          if (location.pathname !== "/" && (!publicPageIsAccessible || redirectOnError)) {
            history.push("/", { from: history.location.pathname });
          }

          return false;
        }
      } catch (error) {
        debug.error("SIGNED OUT:", error);
        dispatch(setGlobalUser(null));
        dispatch(purgeUsers());

        if (location.pathname !== "/" && (!publicPageIsAccessible || redirectOnError)) {
          history.push("/", { from: history.location.pathname });
        }
      }

      return false;
    },
    [
      checkCognitoGroup,
      loadLoginEntities,
      dispatch,
      redirectOnError,
      history
    ]
  );

  /**
   * Send confirmation of the verification code and login
   */
  const confirmMFACode = useCallback((mfaCode: string) => {
    setUserAuthState({
      ...userAuthState,
      loggingIn: true,
      error: null
    });

    Auth.confirmSignIn({ challengeResponse: mfaCode })
      .then(async signInResult => {
        setUserAuthState({
          ...userAuthState,
          challengeName: signInResult.nextStep.signInStep
        });

        if (signInResult.isSignedIn) {
          const session = await Auth.fetchAuthSession();

          if (checkCognitoGroup(session)) {
            setTimeout(() => {
              loadLoginEntities();
            }, 300);

            return;
          }
        }
      })
      .catch((err: AmplifyError) => {
        const parsedErr = parseAmplifyError(err);

        setUserAuthState({
          ...userAuthState,
          error: parsedErr.details,
          loggingIn: false
        });
      });
  }, [
    checkCognitoGroup,
    loadLoginEntities,
    userAuthState
  ]);

  // Does the auth check
  // and handles new password journey
  const login = (username: string, password: string) => {
    // make sure the caches are cleared before they attempt to login
    clearCaches();
    // always use a lowercase version of email with spaces replaced
    username = username.replace(/\s+/g, "").toLowerCase();

    setUserAuthState({
      ...userAuthState,
      loggingIn: true,
      error: null
    });

    Auth.signIn({
      username,
      password,
      options: { authFlowType: "USER_PASSWORD_AUTH" }
    })
      .then(async signInResult => {
        debug.info(signInResult, signInResult.nextStep.signInStep);
        gtmEvent("login", { method: "email" });

        if (signInResult.nextStep.signInStep && (signInResult.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE" || signInResult.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE")) {
          setUserAuthState({
            ...userAuthState,
            challengeName: signInResult.nextStep.signInStep
          });
        } else if (signInResult.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
          setUserAuthState({
            ...userAuthState,
            challengeName: signInResult.nextStep.signInStep
          });
        } else if (signInResult.isSignedIn) {
          const session = await Auth.fetchAuthSession();

          if (checkCognitoGroup(session)) {
            setTimeout(() => {
              loadLoginEntities();
            }, 300);

            return;
          }

          setUserAuthState({
            ...userAuthState,
            error: "Not authorized to access this portal",
            loggingIn: false
          });
          dispatch(setGlobalUser(null));
          Auth.signOut();

          return false;
        } else {
          if ([
            "CONTINUE_SIGN_IN_WITH_TOTP_SETUP",
            "CONFIRM_SIGN_IN_WITH_TOTP_CODE",
            "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE",
            "CONFIRM_SIGN_IN_WITH_SMS_CODE",
            "CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION"
          ].includes(signInResult.nextStep.signInStep)) {
            debug.error("Challenge mode", signInResult.nextStep.signInStep);

            setUserAuthState({
              ...userAuthState,
              error: "Challenge type for sign in is not permitted"
            });
          }
        }

        return false;
      })
      .catch((err: AmplifyError) => {
        const parsedErr = parseAmplifyError(err);

        debug.error("SIGN IN", err);

        setUserAuthState({
          ...userAuthState,
          error: parsedErr.details || defaultError,
          loggingIn: false
        });

        return false;
      });

    return;
  };

  const loginWithMicrosoft = (idpName?: string) => {
    // make sure the caches are cleared before they attempt to login
    clearCaches();

    setUserAuthState({
      ...userAuthState,
      socialLoggingIn: true,
      error: null
    });

    const customProvider = idpName || `Mezze-${process.env.REACT_APP_ENVIRONMENT === "production" ? "Production" : "Staging"}-Azure-IDP` ;

    Auth.signInWithRedirect({ provider: { custom: customProvider } })
      .then(() => {
        gtmEvent("login", { method: "microsoft" });
      });

    return;
  };

  const loginWithGoogle = () => {
    // make sure the caches are cleared before they attempt to login
    clearCaches();

    setUserAuthState({
      ...userAuthState,
      socialLoggingIn: true,
      error: null
    });

    Auth.signInWithRedirect({ provider: "Google" })
      .then(() => {
        gtmEvent("login", { method: "google" });
      });

    return;
  };

  const forgotPassword = (username: string) => {
    // always use a lowercase version of email with spaces replaced
    username = username.replace(/\s+/g, "").toLowerCase();

    Auth.resetPassword({ username })
      .then(() => {
        setUserAuthState({
          ...userAuthState,
          resettingPassword: true,
          error: null
        });
      })
      .catch((err: AmplifyError) => {
        const parsedErr = parseAmplifyError(err);

        if (err.message === "User password cannot be reset in the current state.") {
          setUserAuthState({
            ...userAuthState,
            error: parsedErr.details
          });
          resendInvite({ email: username });
        } else {
          setUserAuthState({
            ...userAuthState,
            resettingPassword: false,
            error: parsedErr.details
          });
        }
      });
  };

  const confirmResetForgottenPassword = (username: string, code: string, newPassword: string) => {
    // always use a lowercase version of email with spaces replaced
    username = username.replace(/\s+/g, "").toLowerCase();

    Auth.confirmResetPassword({
      username,
      confirmationCode: code,
      newPassword
    })
      .then(() => {
        setUserAuthState({
          ...userAuthState,
          resettingPassword: false,
          error: null
        });
        login(username, newPassword);
      })
      .catch((err: AmplifyError) => {
        const parsedErr = parseAmplifyError(err);

        setUserAuthState({
          ...userAuthState,
          resettingPassword: true,
          error: parsedErr.details
        });
      });
  };

  const clearCaches = async () => {
    // Register the custom `memoryDriver` to `localforage`
    await localforage.defineDriver(memoryDriver);

    // Create `localforage` instance
    const forageStore = localforage.createInstance({
      // List of drivers used
      driver: [
        localforage.INDEXEDDB,
        localforage.LOCALSTORAGE,
        memoryDriver._driver
      ],
      // Prefix all storage keys to prevent conflicts
      name: "mezze-ui"
    });

    forageStore.clear();
  };

  const logout = async () => {
    saveTableSettings("{}");
    saveNewTableSettings("{}");
    clearCaches();
    await Auth.signOut();
    dispatch(purgeUserRole());
    dispatch(purgeUsers());
    dispatch(purgeCustomers());
    dispatch(purgeDrops());
    dispatch(purgeOrders());
    dispatch(purgeProducts());
    dispatch(purgeNotifications());
    dispatch(purgeLists());
    dispatch(purgeSchedules());
    dispatch(purgeAnalytics());
    dispatch(setMasqueradeMode({ active: false }));
    setTags("[]");

    // timeout just to allow time for stores to clear
    setTimeout(() => {
      setUserAuthState({
        ...userAuthState,
        loggedOut: true,
        loggingOut: false
      });
    }, 500);
  };

  return [
    {
      login,
      checkLoggedIn,
      confirmNewPassword,
      confirmMFACode,
      forgotPassword,
      confirmResetForgottenPassword,
      logout,
      loginWithMicrosoft,
      loginWithGoogle
    },
    userAuthState
  ];
};
